import { HttpClient, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NotificationViewModalComponent } from '@modules/backoffice/notification-context/notification/consult/notification-view-modal.component';
import { Pagination } from '@common/const/search-filter.constants';
import { UserProfile } from '@common/model/user-profile.model';
import { ReadService } from '@common/service/read.service';
import { AuthService } from '@core/auth/auth.service';
import { EntityResponseType } from '@core/service/generic.service';
import { environment } from '@environments/environment';
import { BehaviorSubject, interval, Observable, Subject, Subscription, tap, throwError } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { catchError, takeWhile } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class NotificationPollingService {
  private static readonly MAX_WARNING_TOASTS = 3; // au-dela : un seul toast recapitulatif
  private static readonly WARNING_TOAST_TIMEOUT = 8000; // disparition auto apres 8 s
  private static readonly DISPLAYED_WARNINGS_KEY = 'tmw.displayedWarningIds'; // suffixe par utilisateur
  private static readonly DISPLAYED_WARNINGS_MAX = 500;

  private notifications$ = new BehaviorSubject<any[]>([]);
  private appEvent$ = new Subject<string>();
  private readonly userInfo$ = new BehaviorSubject<UserProfile | undefined>(undefined);
  // Toasts deja affiches, persistes pour ne pas les rejouer apres un refresh de la page
  private displayedWarningIds = new Set<number>();
  private readonly router = inject(Router);
  private readonly modalService = inject(NgbModal);

  protected resourceUrl = `${environment.SERVER_NOTIFICATION_API_URL}/api/notifications`;

  private pollingSub?: Subscription;
  page = Pagination.PageStart;
  pageSize = Pagination.PageSize;
  totalCount = 0;

  constructor(
    private readonly readService: ReadService,
    protected http: HttpClient,
    private readonly authService: AuthService,
    private readonly toastr: ToastrService
  ) {
    this.authService.getProfile().subscribe({
      next: (profile) => {
        this.userInfo$.next(profile);
        this.displayedWarningIds = this.loadDisplayedWarningIds();
        this.startPolling();
      },
      error: (err) => {
        console.error('Erreur lors du chargement du profil utilisateur', err);
      },
    });
  }

  post(element: any): Observable<HttpResponse<any>> {
    return this.http.post<any>(this.resourceUrl, element, {
      observe: 'response',
    });
  }

  getNotifications(): Observable<any[]> {
    return this.notifications$.asObservable();
  }

  getAppEvents(): Observable<string> {
    return this.appEvent$.asObservable();
  }

  private startPolling(): void {
    this.loadNotifications();
    this.pollingSub = interval(120000).pipe(takeWhile(() => this.authService.isAuthenticated())).subscribe(() => this.loadNotifications());
  }

  /* ngOnDestroy(): void {
    if (this.pollingSub) {
      this.pollingSub.unsubscribe();
    }
  }*/

  private loadNotifications(): void {
    const userInfo = this.userInfo$.getValue();
    const roles = userInfo?.roles ?? [];
    const utilisateurId = userInfo?.id ?? null;

    if (!roles.length || utilisateurId == null) {
      console.warn('Polling notifications annulé : roles vide ou utilisateurId nul');
      this.notifications$.next([]);
      this.totalCount = 0;
      return;
    }

    this.readService
      .queryPaged('listNotificationsUtilisateur', {
        params: {
          roles: roles,
          utilisateurId: utilisateurId,
        },
        page: {
          page: this.page - 1,
          size: this.pageSize,
        },
      })
      .subscribe({
        next: (result) => {
          const records = result?.records ?? [];
          this.totalCount = result?.totalCount ?? 0;
          this.notifications$.next(records);

          // Show warning toast for TNT_AVERTISSEMENT notifications
          const newWarnings = records.filter(
            (notif) => notif.typeNotificationCode === 'TNT_AVERTISSEMENT' && !notif.lu && !this.displayedWarningIds.has(notif.id)
          );
          if (newWarnings.length) {
            newWarnings.forEach((notif) => this.displayedWarningIds.add(notif.id));
            this.saveDisplayedWarningIds();
          }

          if (newWarnings.length > NotificationPollingService.MAX_WARNING_TOASTS) {
            // Trop d'avertissements d'un coup : un seul toast recapitulatif au lieu d'une pile qui masque l'ecran
            this.showWarningToast(
              `${newWarnings.length} fichiers ont été traités. Consultez les notifications pour le détail.`,
              newWarnings[0].titre || 'Avertissement',
              newWarnings.map((notif) => notif.id),
              () => this.router.navigate(['backoffice/notification-context/notifications-list'])
            );
          } else {
            newWarnings.forEach((notif) =>
              this.showWarningToast(
                this.extractTextFromHtml(notif.contenu || '', 200),
                notif.titre || 'Avertissement',
                [notif.id],
                () => this.openNotificationModal(notif)
              )
            );
          }

          const events: Record<string, boolean> = {};
          records.forEach((notif: any) => {
            if (!notif.lu && notif.evenementCode) {
              events[notif.evenementCode] = true;
            }
          });
          Object.keys(events).forEach((code) => this.appEvent$.next(code));
        },
        error: (err) => console.error('Erreur de polling notifications', err),
      });
  }

  update(element: any): Observable<EntityResponseType> {
    return this.http
      .patch<any>(`${this.resourceUrl}/${element.id}`, element, {
        observe: 'response',
      })
      .pipe(
        tap(() => this.loadNotifications()),
        catchError((error) => {
          console.error('Erreur lors de la mise à jour :', error);
          return throwError(() => error); // Propager l'erreur
        })
      );
  }

  private displayedWarningsKey(): string {
    return `${NotificationPollingService.DISPLAYED_WARNINGS_KEY}.${this.userInfo$.getValue()?.id}`;
  }

  private loadDisplayedWarningIds(): Set<number> {
    try {
      const ids = JSON.parse(localStorage.getItem(this.displayedWarningsKey()) ?? '[]');
      return new Set<number>(Array.isArray(ids) ? ids : []);
    } catch {
      return new Set<number>();
    }
  }

  private saveDisplayedWarningIds(): void {
    try {
      // On ne garde que les plus recents pour borner la taille du stockage
      const ids = [...this.displayedWarningIds].slice(-NotificationPollingService.DISPLAYED_WARNINGS_MAX);
      localStorage.setItem(this.displayedWarningsKey(), JSON.stringify(ids));
    } catch {
      // stockage indisponible (navigation privee...) : on garde le suivi en memoire uniquement
    }
  }

  private showWarningToast(message: string, title: string, notificationIds: number[], onClick: () => void): void {
    const toastRef = this.toastr.warning(message, title, {
      positionClass: 'toast-top-full-width',
      timeOut: NotificationPollingService.WARNING_TOAST_TIMEOUT,
      extendedTimeOut: 3000, // survol = pause, puis 3 s apres sortie de la souris
      progressBar: true,
      closeButton: true,
      tapToDismiss: true, // clic = ouvre le detail puis ferme le toast
      enableHtml: false,
    });

    toastRef.onTap.subscribe(onClick);

    toastRef.onHidden.subscribe(() => {
      notificationIds
        .filter(Boolean)
        .forEach((id) =>
          this.updateWithoutReload({ id, lu: true }).subscribe({
            error: (err) => console.error('Erreur lors de la mise à jour du statut de lecture:', err),
          })
        );
    });
  }

  private openNotificationModal(notif: any): void {
    const modalRef = this.modalService.open(NotificationViewModalComponent, { size: 'lg', centered: true });
    modalRef.componentInstance.notification = {
      ...notif,
      timestamp: notif.dateCreation ? new Date(notif.dateCreation) : new Date(),
      background: 'bg-warning-subtle text-warning',
      icon: 'bi bi-exclamation-triangle',
    };
  }

  private updateWithoutReload(element: any): Observable<EntityResponseType> {
    return this.http
      .patch<any>(`${this.resourceUrl}/${element.id}`, element, {
        observe: 'response',
      })
      .pipe(
        catchError((error) => {
          console.error('Erreur lors de la mise à jour :', error);
          return throwError(() => error);
        })
      );
  }

  delete(id: number): Observable<HttpResponse<{}>> {
    return this.http.delete(`${this.resourceUrl}/${id}`, { observe: 'response' }).pipe(
      tap(() => this.loadNotifications()),
      catchError((error) => {
        console.error('Erreur lors de la suppression :', error);
        return throwError(() => error); // Propager l'erreur
      })
    );
  }


  private extractTextFromHtml(html: string, maxLength: number = 200): string {
    if (!html) return '';
    
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    
    const text = tempDiv.textContent || tempDiv.innerText || '';
    
    if (text.length > maxLength) {
      return text.substring(0, maxLength).trim() + '...';
    }
    
    return text.trim();
  }
}
